import React, { useState, useEffect } from 'react';
import { FiEdit2, FiTrash2, FiPlus, FiSave, FiX } from 'react-icons/fi';
import { gsidaSpeakersApi, fileUploadApi } from '../../api/phpApi';
import './AdminManage.css';

export default function AdminSpeakers() {
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [logoPreview, setLogoPreview] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    country_code: '',
    country: '',
    company: '',
    company_logo: '',
    domain: '',
    image: '',
    bio: ''
  });

  // Fetch speakers
  useEffect(() => {
    loadSpeakers();
  }, []);

  const loadSpeakers = async () => {
    try {
      setLoading(true);
      const data = await gsidaSpeakersApi.getAll();
      setSpeakers(data || []);
    } catch (err) {
      setError('Failed to load speakers: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle image upload
  const handleImageUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const result = await fileUploadApi.upload(file);
      const url = result.url;

      if (type === 'profile') {
        setFormData(prev => ({ ...prev, image: url }));
        setImagePreview(url);
      } else {
        setFormData(prev => ({ ...prev, company_logo: url }));
        setLogoPreview(url);
      }
    } catch (err) {
      setError('Failed to upload image: ' + err.message);
    }
  };

  // Handle add/edit submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await gsidaSpeakersApi.update(editingId, formData);
        setError('');
      } else {
        await gsidaSpeakersApi.create(formData);
        setError('');
      }
      resetForm();
      loadSpeakers();
    } catch (err) {
      setError('Failed to save speaker: ' + err.message);
    }
  };

  // Handle edit
  const handleEdit = (speaker) => {
    setFormData(speaker);
    setImagePreview(speaker.image);
    setLogoPreview(speaker.company_logo);
    setEditingId(speaker.id);
    setIsFormOpen(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this speaker?')) {
      try {
        await gsidaSpeakersApi.delete(id);
        loadSpeakers();
      } catch (err) {
        setError('Failed to delete speaker: ' + err.message);
      }
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      title: '',
      country_code: '',
      country: '',
      company: '',
      company_logo: '',
      domain: '',
      image: '',
      bio: ''
    });
    setImagePreview('');
    setLogoPreview('');
    setEditingId(null);
    setIsFormOpen(false);
  };

  if (loading) return <div className="admin-loading">Chargement des intervenants...</div>;

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Gérer les Intervenants GSIDA</h2>
        <button 
          className="btn-primary btn-add"
          onClick={() => setIsFormOpen(true)}
        >
          <FiPlus /> Ajouter un Intervenant
        </button>
      </div>

      {error && <div className="error-alert">{error}</div>}

      {/* Form Modal */}
      {isFormOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingId ? 'Modifier l\'Intervenant' : 'Ajouter un Nouvel Intervenant'}</h3>
              <button className="btn-close" onClick={resetForm}>
                <FiX />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="admin-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Nom *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Titre/Position *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Code Pays (ex: ma, fr)</label>
                  <input
                    type="text"
                    name="country_code"
                    value={formData.country_code}
                    onChange={handleInputChange}
                    maxLength="3"
                  />
                </div>
                <div className="form-group">
                  <label>Pays</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Entreprise</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label>Domaine/Expertise</label>
                  <input
                    type="text"
                    name="domain"
                    value={formData.domain}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Biographie</label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Image de Profil</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'profile')}
                  />
                  {imagePreview && (
                    <img src={imagePreview} alt="Aperçu" className="image-preview" />
                  )}
                </div>
                <div className="form-group">
                  <label>Logo de l'Entreprise</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'logo')}
                  />
                  {logoPreview && (
                    <img src={logoPreview} alt="Aperçu Logo" className="image-preview" />
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  <FiSave /> Sauvegarder l'Intervenant
                </button>
                <button type="button" className="btn-secondary" onClick={resetForm}>
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Speakers Table */}
      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Titre</th>
              <th>Pays</th>
              <th>Entreprise</th>
              <th>Domaine</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {speakers.length === 0 ? (
              <tr><td colSpan="6" className="text-center">Aucun intervenant trouvé</td></tr>
            ) : (
              speakers.map(speaker => (
                <tr key={speaker.id}>
                  <td>{speaker.name}</td>
                  <td>{speaker.title}</td>
                  <td>{speaker.country}</td>
                  <td>{speaker.company}</td>
                  <td>{speaker.domain}</td>
                  <td className="actions">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(speaker)}
                      title="Modifier"
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(speaker.id)}
                      title="Supprimer"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
